import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

function getStoredTokens() {
  return {
    access: localStorage.getItem("profimy_access_token"),
    refresh: localStorage.getItem("profimy_refresh_token"),
  };
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem("profimy_access_token", access);
  if (refresh) localStorage.setItem("profimy_refresh_token", refresh);
}

function clearTokens() {
  localStorage.removeItem("profimy_access_token");
  localStorage.removeItem("profimy_refresh_token");
}

function authHeaders() {
  const token = localStorage.getItem("profimy_access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const tokens = getStoredTokens();
    if (!tokens.access && !tokens.refresh) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setUser(res.data);
    } catch {
      if (tokens.refresh) {
        try {
          const refreshRes = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Refresh ${tokens.refresh}` },
          });
          if (refreshRes.data.access_token) {
            storeTokens(refreshRes.data.access_token, null);
          }
          setUser(refreshRes.data);
        } catch {
          clearTokens();
          setUser(false);
        }
      } else {
        clearTokens();
        setUser(false);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    storeTokens(res.data.access_token, res.data.refresh_token);
    localStorage.setItem("user_email", res.data.email || email);
    const { access_token, refresh_token, ...userData } = res.data;
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    storeTokens(res.data.access_token, res.data.refresh_token);
    localStorage.setItem("user_email", res.data.email || email);
    const { access_token, refresh_token, ...userData } = res.data;
    setUser(userData);
    return userData;
  }, []);

  const googleAuth = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/google`, { session_id: sessionId });
    storeTokens(res.data.access_token, res.data.refresh_token);
    localStorage.setItem("user_email", res.data.email || "");
    const { access_token, refresh_token, ...userData } = res.data;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
    } catch { /* ignore */ }
    clearTokens();
    localStorage.removeItem("user_email");
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleAuth, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { authHeaders };
